export const analyticsGoals = {
  heroGenplanClick: 'hero_genplan_click',
  lotDetailsOpen: 'lot_details_open',
  lotSelectClick: 'lot_select_click',
  purchaseTermsClick: 'purchase_terms_click',
  telegramClick: 'telegram_click',
  routeClick: 'route_click',
  faqOpen: 'faq_open',
  plotCallClick: 'plot_call_click',
  plotShareSuccess: 'plot_share_success',
  plotLinkCopy: 'plot_link_copy',
} as const;

export type AnalyticsGoal = (typeof analyticsGoals)[keyof typeof analyticsGoals];
export type AnalyticsConsent = 'pending' | 'granted' | 'denied';
export type AnalyticsGoalParameters = Record<string, boolean | number | string>;

const scriptId = 'yandex-metrika-script';
const rawCounterId = import.meta.env.VITE_YANDEX_METRIKA_ID?.trim();
const counterId = rawCounterId && /^\d+$/.test(rawCounterId) ? Number(rawCounterId) : undefined;

let consent: AnalyticsConsent = 'granted';
let initialized = false;
let scheduledIdleCallback: number | undefined;
let scheduledTimeout: number | undefined;

function ensureQueue() {
  if (window.ym) return window.ym;

  const queuedCalls: unknown[][] = [];
  const queue: YandexMetrikaFunction = (...args) => {
    queuedCalls.push(args);
  };
  queue.a = queuedCalls;
  queue.l = Date.now();
  window.ym = queue;
  return queue;
}

function appendScript() {
  scheduledIdleCallback = undefined;
  scheduledTimeout = undefined;

  if (consent !== 'granted' || document.getElementById(scriptId)) return;

  const script = document.createElement('script');
  script.id = scriptId;
  script.src = 'https://mc.yandex.ru/metrika/tag.js';
  script.async = true;
  script.onerror = () => {
    script.remove();
  };
  document.head.append(script);
}

function scheduleScript() {
  if (scheduledIdleCallback !== undefined || scheduledTimeout !== undefined) return;

  if ('requestIdleCallback' in window) {
    scheduledIdleCallback = window.requestIdleCallback(appendScript, { timeout: 1800 });
    return;
  }

  scheduledTimeout = globalThis.setTimeout(appendScript, 900);
}

function cancelScheduledScript() {
  if (scheduledIdleCallback !== undefined && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(scheduledIdleCallback);
    scheduledIdleCallback = undefined;
  }

  if (scheduledTimeout !== undefined) {
    globalThis.clearTimeout(scheduledTimeout);
    scheduledTimeout = undefined;
  }
}

function initialize() {
  if (!import.meta.env.PROD || !counterId || initialized || consent !== 'granted') return;

  initialized = true;
  ensureQueue()(counterId, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: false,
  });
  scheduleScript();
}

export function bootstrapYandexMetrika() {
  initialize();
}

export function setAnalyticsConsent(nextConsent: AnalyticsConsent) {
  consent = nextConsent;

  if (nextConsent === 'granted') initialize();
  else cancelScheduledScript();
}

export function getAnalyticsConsent() {
  return consent;
}

export function trackAnalyticsGoal(goal: AnalyticsGoal, parameters?: AnalyticsGoalParameters) {
  if (!import.meta.env.PROD || !counterId || consent !== 'granted') return;

  initialize();
  window.ym?.(counterId, 'reachGoal', goal, parameters);
}
